import { getAuth } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { Heading } from 'src/components/Heading';
import { Loading } from 'src/components/Loading';
import { Option } from 'src/components/SelectBox';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Organization } from 'src/interfaces/organization.interface';
import { getPayloadJWT, isJWT } from 'src/utils/jwt';

export const Organizations = () => {
  const { get } = useAuthedRequest();
  const notification = useNotification();
  const auth = getAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Option | undefined>(undefined);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [pendingOrganization, setPendingOrganization] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!userId) return;

    setLoading(true);
    (async () => {
      try {
        const orgs: Organization[] = await get(
          `/users/${userId}/organizations`,
        );
        setOrganizations(orgs);
      } catch {
        notification.error('Failed to fetch organizations');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, get, notification]);

  useEffect(() => {
    async function fetchCurrentUserAndOrganization() {
      const token = await auth.currentUser?.getIdToken();

      if (token && isJWT(token)) {
        const decoded = getPayloadJWT(token);

        if (decoded.id) {
          setUserId(decoded.id);
        }

        if (decoded.organization) {
          setCurrentOrg({
            label: decoded.organization?.name || '',
            value: decoded.organization?.id || '',
          });
        }
      }
    }

    fetchCurrentUserAndOrganization();
  }, [auth.currentUser]);

  const handleChangeOrganization = (orgId: string) => {
    if (orgId === currentOrg?.value) {
      setPendingOrganization(null);
      return;
    }

    setPendingOrganization(orgId);
  };

  const handleConfirmSwitch = () => {
    if (!pendingOrganization) {
      navigate('/');
      return;
    }

    navigate(`/org-switch?orgId=${pendingOrganization}&returnTo=/`);
    setPendingOrganization(null);
  };

  const fetchOrganizationsOptions = async (query: string) => {
    return organizations
      .filter((org) => org.name.toLowerCase().includes(query.toLowerCase()))
      .map((org) => ({
        label: org.name,
        value: org.id,
      }));
  };

  return (
    <div className="flex-1">
      <Heading title="Organizations" subtitle="Select your organization" />

      {loading && <Loading className="p-4" />}

      {!loading && (
        <ContentWrapper>
          <div className="max-w-md">
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-600 mb-2">
                Select Organization
              </label>
              <AutoCompleteSelectBox
                key={
                  String(currentOrg?.value) + '-' + String(currentOrg?.label)
                }
                fetchOptions={fetchOrganizationsOptions}
                onChange={(value) => handleChangeOrganization(value)}
                className="w-full"
                variant="outlined"
                size="medium"
                disabled={loading}
                defaultOptions={organizations.map((org) => ({
                  label: org.name,
                  value: org.id,
                }))}
                allowRemoveOption={false}
                placeholder="Select Organization..."
                defaultValue={currentOrg}
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleConfirmSwitch}
                disabled={loading}
                color="primary"
                className="flex-1"
              >
                Continue
              </Button>
            </div>
          </div>
        </ContentWrapper>
      )}
    </div>
  );
};
